import { useEffect, useRef, useState } from "react";
import type { PositionAction, PositionActions } from "../api/usePositionActions";
import {
  automaticExitLine,
  ENGINE_OWNED_EXITS,
  OPERATOR_OWNED_EXITS,
} from "../lib/operationPresentation";
import { AUTOPILOT_MANUAL_OPEN_BLOCKED_REASON } from "./AutopilotProvider";
import { useAutopilot } from "./autopilotContext";
function Toast({
  action,
  onShow,
  onHistory,
  onHide,
  onRestart,
}: {
  action: PositionAction;
  onShow: () => void;
  onHistory: () => void;
  onHide: () => void;
  onRestart: () => void;
}) {
  const autopilot = useAutopilot();
  const [hovered, setHovered] = useState(false);
  const [focusWithin, setFocusWithin] = useState(false);
  const pair = action.position?.pair ?? action.pair;
  const done = action.operation?.state === "completed";
  // The engine stopped on its own: this is not progress, and no timer may take it away.
  const stoppedExit = action.operation
    ? OPERATOR_OWNED_EXITS.includes(action.operation.automaticExit?.state ?? "")
    : false;
  const exitLine = action.operation ? automaticExitLine(action.operation) : null;
  const paused = hovered || focusWithin;
  const hide = useRef(onHide);
  const remainingMs = useRef(8000);
  const timerStartedAt = useRef<number | null>(null);
  hide.current = onHide;
  useEffect(() => {
    // A stopped sequence is not a success; it stays until the operator acts on it.
    if (!done || action.restart || action.autoReopenStopped) {
      remainingMs.current = 8000;
      timerStartedAt.current = null;
      return;
    }
    if (paused) return;
    timerStartedAt.current = Date.now();
    const timer = window.setTimeout(() => {
      timerStartedAt.current = null;
      remainingMs.current = 0;
      hide.current();
    }, remainingMs.current);
    return () => {
      window.clearTimeout(timer);
      if (timerStartedAt.current !== null) {
        remainingMs.current = Math.max(
          0,
          remainingMs.current - (Date.now() - timerStartedAt.current),
        );
        timerStartedAt.current = null;
      }
    };
  }, [done, paused, action.restart, action.autoReopenStopped]);
  return (
    <div
      className={`operation-toast ${done ? "completed" : "pending"}`}
      onMouseEnter={() => {
        setHovered(true);
      }}
      onMouseLeave={() => {
        setHovered(false);
      }}
      onFocus={() => {
        setFocusWithin(true);
      }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setFocusWithin(false);
      }}
      onKeyDown={(e) => {
        if (e.key !== "Escape") return;
        e.preventDefault();
        e.stopPropagation();
        onHide();
      }}
    >
      <div className="operation-toast-content">
        <b>
          {done
            ? action.autoReopenStopped
              ? "Automatic reopen stopped"
              : action.cleanup
                ? "Opening cancelled"
                : "Position closed"
            : stoppedExit
              ? `Automatic close stopped · ${pair ?? "position"}`
              : `${action.cleanup ? "Cancelling" : "Closing"} ${pair ?? "position"}`}
        </b>
        <small>
          {done
            ? (pair ?? "Pair unavailable")
            : stoppedExit
              ? (exitLine ?? "The stop is no longer closing this position")
              : "Progress is shown on the card"}
        </small>
      </div>
      <div className="operation-toast-actions">
        {done ? (
          <>
            <button className="button ghost small" onClick={onHistory}>
              History
            </button>
            {action.restart && (
              <button
                className="button small"
                onClick={onRestart}
                disabled={autopilot.blocksManualOpening}
                title={
                  autopilot.blocksManualOpening ? AUTOPILOT_MANUAL_OPEN_BLOCKED_REASON : undefined
                }
              >
                Set up again
              </button>
            )}
          </>
        ) : (
          <button className="button ghost small" onClick={onShow}>
            Show
          </button>
        )}
        <button
          type="button"
          className="operation-toast-dismiss"
          aria-label="Dismiss notification"
          onClick={onHide}
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>
    </div>
  );
}
export function OperationToasts({
  controller,
  onShow,
  onHistory,
  onRestart,
}: {
  controller: PositionActions;
  onShow: (id: string) => void;
  onHistory: (tab: "closed" | "operations") => void;
  onRestart: (id: string) => void;
}) {
  return (
    <div className="operation-toasts" aria-live="polite" aria-relevant="additions text">
      {Object.entries(controller.actions)
        .filter((entry): entry is [string, PositionAction] => {
          const [, a] = entry;
          if (!a?.operation || a.hidden) return false;
          // An automatic exit is judged by what the engine will do next, never by the operation's
          // own state: a stuck close sits in `blocked` or `failed`, and those are exactly the rows
          // the operator has to be told about.
          const exit = a.operation.automaticExit?.state ?? "";
          if (OPERATOR_OWNED_EXITS.includes(exit)) return true;
          if (ENGINE_OWNED_EXITS.includes(exit)) return false;
          return !["blocked", "failed", "unknown", "needs-attention", "cancelled"].includes(
            a.operation.state,
          );
        })
        .map(([id, a]) => (
          <Toast
            key={a.operation?.id}
            action={a}
            onShow={() => {
              onShow(id);
            }}
            onHistory={() => {
              onHistory(a.cleanup ? "operations" : "closed");
            }}
            onHide={() => {
              controller.hideToast(id);
            }}
            onRestart={() => {
              onRestart(id);
            }}
          />
        ))}
    </div>
  );
}
